import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuthenticatedUser, User } from '../_models';
import { environment } from 'src/environments/environment';
import jwt_decode from 'jwt-decode';
import { DebugService } from './debug.service';
import { Subscription } from '../_models/sub';

type JWTHeaders = {
    typ: string;
    alg: string;
};

type JWTPayload = {
    iat: number;
    exp: number;
};

type JWTToken = {
    headers: JWTHeaders;
    payload: JWTPayload;
};

type TokenResponse = {
    token: string;
    refresh_token: string;
};

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
    private rememberMe:boolean = false;

    public accessDeniedCount:number = 0;

    private userSubject: BehaviorSubject<AuthenticatedUser>;
    public user: Observable<AuthenticatedUser>;
    private meSubject: BehaviorSubject<AuthenticatedUser>;
    public me: Observable<User>;

    constructor(private router: Router, private http: HttpClient, private debugService:DebugService) {
        this.userSubject = new BehaviorSubject<AuthenticatedUser>(null);
        this.user = this.userSubject.asObservable();
        this.meSubject = new BehaviorSubject<AuthenticatedUser>(null);
        this.me = this.meSubject.asObservable();
        this.getInitialAuthentication();
    }

    public get userValue(): AuthenticatedUser {
        return this.userSubject.value;
    }

    public get meValue(): AuthenticatedUser {
        return this.meSubject.value;
    }

    public rawToken: TokenResponse;

    public get isAuthenticated(): boolean {
        if (!this.rawToken || !this.rawToken.token) {
            return false;
        }

        const token = this.rawToken.token;
        const headers = jwt_decode<JWTHeaders>(token, { header: true });
        const payload = jwt_decode<JWTPayload>(token);

        const tokenExpired = this.isTokenExpired({ headers, payload });

        return !tokenExpired;
    }

    public get decodedToken(): AuthenticatedUser {
        return jwt_decode<AuthenticatedUser>(this.rawToken.token);
    }

    public get isAdmin(): boolean {
        if (!this.rawToken || !this.rawToken.token) {
            return false;
        }

        const token = this.rawToken.token;
        const headers = jwt_decode<JWTHeaders>(token, { header: true });
        const payload = jwt_decode<JWTPayload>(token);

        const tokenExpired = this.isTokenExpired({ headers, payload });

        if (!this.isTokenExpired({ headers, payload })) {
            let me:AuthenticatedUser = jwt_decode<AuthenticatedUser>(this.rawToken.token);
            return me.permissions['is_admin'] === true;
        }

        return false;
    }

    public get isSubscribed(): boolean {
        if (!this.rawToken || !this.rawToken.token) {
            return false;
        }

        const token = this.rawToken.token;
        const headers = jwt_decode<JWTHeaders>(token, { header: true });
        const payload = jwt_decode<JWTPayload>(token);

        const tokenExpired = this.isTokenExpired({ headers, payload });

        if (!this.isTokenExpired({ headers, payload })) {
            let me:AuthenticatedUser = jwt_decode<AuthenticatedUser>(this.rawToken.token);
            return me.roles.includes('ROLE_USER_BRANCHE') || me.roles.includes('ROLE_USER_GEEK') || me.roles.includes('ROLE_USER_ADMIN');
        }

        return false;
    }

    public get isSubscribedGeek(): boolean {
        if (!this.isSubscribed) {
            return false;
        }

        let me:AuthenticatedUser = jwt_decode<AuthenticatedUser>(this.rawToken.token);
        return me.roles.includes('ROLE_USER_GEEK');
    }

    public get isSubscribedBranche(): boolean {
        if (!this.isSubscribed) {
            return false;
        }

        let me:AuthenticatedUser = jwt_decode<AuthenticatedUser>(this.rawToken.token);
        return me.roles.includes('ROLE_USER_BRANCHE');
    }

    public get lastSubscription(): Subscription {
        let subscription = this.meValue.subscriptions.filter(item => item.status != 'pending' && item.status != 'canceled_admin').sort((a: Subscription, b: Subscription) => {
            return (
                new Date(b.updatedAt).getTime() -
                new Date(a.updatedAt).getTime()
            );
        });

        return subscription[0];
    }

    init() {
        // const rawToken = localStorage.getItem('token');
        // const refreshToken = localStorage.getItem('refresh_token');
        // if (null === rawToken || null === refreshToken) {
        //     return;
        // }

        // this.manageToken({
        //     token: rawToken,
        //     refresh_token: refreshToken
        // })
    }

    login(username: string, password: string, broadcast:boolean = true, rememberMe:boolean = true) {
        this.rememberMe = rememberMe;
        return this.http
        .post<any>(
            `${environment.apiUrl}/login_check`,
            { username, password },
            { withCredentials: false }
        )
        .pipe(map((data: TokenResponse) => this.manageToken(data, broadcast)));
    }

    checkPassword(username: string, password: string) {
        return this.http
        .post<any>(
            `${environment.apiUrl}/login_check`,
            { username, password },
            { withCredentials: false }
        )
        .pipe(map((data: TokenResponse) => {
            let rawToken = data;

            let user:AuthenticatedUser = jwt_decode<AuthenticatedUser>(rawToken.token)
            if (user.roles.includes('ROLE_USER_VALIDATED')) {
                return true;
            }

            return false;
        }));
    }

    requestPassword(username: string) {
        let formData = new FormData();
        formData.append("email", username);
        return this.http
        .post<any>(
            `${environment.apiUrl}/users/reset-password/request`,
            formData,
            { withCredentials: false }
        );
    }

    requestPasswordToken() {
        let formData = new FormData();
        return this.http
            .post<any>(
                `${environment.apiUrl}/users/request-token`,
                formData,
                { withCredentials: false }
            );
    }

    resetPassword(password: string, token:string, oldPassword:string = null) {
        return this.http
        .post<any>(
            `${environment.apiUrl}/users/reset-password/update`,
            {
                password: password,
                oldPassword: oldPassword,
                token: token
            },
            { withCredentials: false }
        );
    }

    async logout(redirect:boolean = true) {
        await this.http.get<any>(`${environment.websiteUrl}/chat/discovery/destroy`,
            { withCredentials: true }
        ).subscribe();

        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
        
        this.stopRefreshTokenTimer();
        this.rawToken = null;
        this.userSubject.next(null);
        this.meSubject.next(null);

        if (redirect) {
            this.router.navigate(['/welcome']);
        }
    }

    async softLogout(url:string = '') {
        await this.http.get<any>(`${environment.websiteUrl}/chat/discovery/destroy`,
            { withCredentials: true }
        ).subscribe();
        this.stopRefreshTokenTimer();
        this.userSubject.next(null);
        this.router.navigate(['/welcome'], {
            queryParams: { returnUrl: url }
        });
    }

    refreshToken(broadcast:boolean = false) {
        return this.http
        .post<any>(
            `${environment.apiUrl}/token/refresh`,
            { refresh_token: this.rawToken?.refresh_token },
            { withCredentials: true }
        )
        .pipe(map((data: TokenResponse) => this.manageToken(data, broadcast)));
    }

    loadMe() {
        return this.http.get<AuthenticatedUser>(`${environment.apiUrl}${this.userValue['@id']}`, {
            withCredentials: true
        }).pipe(
            map((data: AuthenticatedUser) => {
                this.meSubject.next(data);
            })
        );
    }

    public saveToken() {
        localStorage.setItem('token', this.rawToken.token);
        localStorage.setItem('refresh_token', this.rawToken.refresh_token);
    }

    private manageToken(data: TokenResponse, broadcast:boolean = true): TokenResponse {
        this.rawToken = data;

        let user:AuthenticatedUser = jwt_decode<AuthenticatedUser>(this.rawToken.token)

        if (user.roles.includes('ROLE_USER_VALIDATED')) {
            if (this.rememberMe) {
                this.saveToken();
            }
        }

        if (broadcast) {
            this.userSubject.next(user);
        }

        this.startRefreshTokenTimer();

        return data;
    }

    private getInitialAuthentication(): void {
        if ('undefined' === typeof localStorage) {
            return;
        }

        const rawToken = localStorage.getItem('token');
        const refreshToken = localStorage.getItem('refresh_token');
        if (null === rawToken || null === refreshToken) {
            return;
        }

        this.userSubject.next(null);

        this.rawToken = {
            token: null,
            refresh_token: null
        };

        this.rememberMe = true;

        const headers = jwt_decode<JWTHeaders>(rawToken, { header: true });
        const payload = jwt_decode<JWTPayload>(rawToken);
        if (this.isTokenExpired({ headers, payload })) {
            this.rawToken.refresh_token = refreshToken;
        }

        this.rawToken.token = rawToken;
        this.rawToken.refresh_token = refreshToken;
    }

    private refreshTokenTimeout;

    private startRefreshTokenTimer() {
        const headers = jwt_decode(this.rawToken.token, { header: true });
        const payload = jwt_decode(this.rawToken.token) as JWTPayload;
        
        // set a timeout to refresh the token a minute before it expires
        const expires = new Date(payload.exp * 1000);
        const timeout = expires.getTime() - Date.now() - 60 * 1000;

        this.debugService.send("Token expire: " + expires);
        
        this.refreshTokenTimeout = setTimeout(
            () => this.refreshToken().subscribe(),
            timeout > 0 ? Math.min(timeout, 1000 * 60 * 60 * 2) : 1000
        );
    }

    private stopRefreshTokenTimer() {
        clearTimeout(this.refreshTokenTimeout);
    }

    private isTokenExpired(token: JWTToken): boolean {
        return token.payload.exp <= Math.round(new Date().getTime() / 1000);
    }
}
